import { FirebaseError } from 'firebase/app';

// Define error types for better type checking
export type FirebaseAuthErrorCode = 
  | 'auth/invalid-credential'
  | 'auth/user-not-found'
  | 'auth/wrong-password'
  | 'auth/email-already-in-use'
  | 'auth/weak-password'
  | 'auth/invalid-email'
  | 'auth/too-many-requests'
  | 'auth/network-request-failed'
  | 'auth/popup-closed-by-user'
  | 'auth/requires-recent-login'
  | 'auth/user-disabled'
  | 'auth/user-token-expired'
  | 'auth/web-storage-unsupported'
  | 'auth/operation-not-allowed'
  | 'auth/account-exists-with-different-credential'
  | 'auth/invalid-verification-code'
  | 'auth/invalid-verification-id'
  | 'auth/missing-verification-code'
  | 'auth/missing-verification-id'
  | 'auth/captcha-check-failed'
  | 'auth/missing-recaptcha-token'
  | 'auth/invalid-recaptcha-token'
  | 'auth/invalid-phone-number'
  | 'auth/missing-phone-number'
  | 'auth/quota-exceeded'
  | 'auth/cancelled-popup-request'
  | 'auth/popup-blocked'
  | 'auth/unauthorized-domain'
  | 'auth/invalid-action-code'
  | 'auth/expired-action-code'
  | 'auth/invalid-continue-uri'
  | 'auth/missing-continue-uri'
  | 'auth/missing-iframe-start'
  | 'auth/missing-android-pkg-name'
  | 'auth/missing-app-credential'
  | 'auth/invalid-app-credential'
  | 'auth/session-expired'
  | string; // For any other error codes not explicitly listed

// English error messages
const errorMessagesEN: Record<FirebaseAuthErrorCode, string> = {
  'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
  'auth/user-not-found': 'No account found with this email address. Please check your email or create a new account.',
  'auth/wrong-password': 'Incorrect password. Please try again or use the forgot password option.',
  'auth/email-already-in-use': 'This email address is already in use. Please use a different email or try logging in.',
  'auth/weak-password': 'Password is too weak. Please use a stronger password with at least 6 characters.',
  'auth/invalid-email': 'Invalid email address format. Please enter a valid email address.',
  'auth/too-many-requests': 'Too many unsuccessful login attempts. Please try again later or reset your password.',
  'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
  'auth/popup-closed-by-user': 'Authentication popup was closed before completing the sign-in process.',
  'auth/requires-recent-login': 'This operation requires a recent login. Please log in again before retrying.',
  'auth/user-disabled': 'This account has been disabled. Please contact support for assistance.',
  'auth/user-token-expired': 'Your session has expired. Please log in again.',
  'auth/web-storage-unsupported': 'This browser does not support web storage. Please try a different browser.',
  'auth/operation-not-allowed': 'This operation is not allowed. Please contact support.',
  'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in credentials.',
  'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
  'auth/invalid-verification-id': 'Invalid verification ID. Please try again.',
  'auth/missing-verification-code': 'Verification code is missing. Please try again.',
  'auth/missing-verification-id': 'Verification ID is missing. Please try again.',
  'auth/captcha-check-failed': 'CAPTCHA verification failed. Please try again.',
  'auth/missing-recaptcha-token': 'reCAPTCHA token is missing. Please try again.',
  'auth/invalid-recaptcha-token': 'Invalid reCAPTCHA token. Please try again.',
  'auth/invalid-phone-number': 'Invalid phone number. Please enter a valid phone number.',
  'auth/missing-phone-number': 'Phone number is missing. Please enter a phone number.',
  'auth/quota-exceeded': 'Quota exceeded. Please try again later.',
  'auth/cancelled-popup-request': 'Only one popup request is allowed at a time.',
  'auth/popup-blocked': 'Popup was blocked by the browser. Please allow popups for this site.',
  'auth/unauthorized-domain': 'This domain is not authorized for OAuth operations.',
  'auth/invalid-action-code': 'Invalid action code. The code may have expired or already been used.',
  'auth/expired-action-code': 'Action code has expired. Please request a new one.',
  'auth/invalid-continue-uri': 'Invalid continue URL. Please try again.',
  'auth/missing-continue-uri': 'Continue URL is missing. Please try again.',
  'auth/missing-iframe-start': 'An internal error has occurred. Please try again later.',
  'auth/missing-android-pkg-name': 'Android package name is missing.',
  'auth/missing-app-credential': 'App credential is missing.',
  'auth/invalid-app-credential': 'Invalid app credential.',
  'auth/session-expired': 'The SMS code has expired. Please re-send the verification code to try again.',
};

// French error messages
const errorMessagesFR: Record<FirebaseAuthErrorCode, string> = {
  'auth/invalid-credential': 'Email ou mot de passe invalide. Veuillez vérifier vos identifiants et réessayer.',
  'auth/user-not-found': 'Aucun compte trouvé avec cette adresse email. Veuillez vérifier votre email ou créer un nouveau compte.',
  'auth/wrong-password': 'Mot de passe incorrect. Veuillez réessayer ou utiliser l\'option mot de passe oublié.',
  'auth/email-already-in-use': 'Cette adresse email est déjà utilisée. Veuillez utiliser une adresse différente ou essayer de vous connecter.',
  'auth/weak-password': 'Le mot de passe est trop faible. Veuillez utiliser un mot de passe plus fort avec au moins 6 caractères.',
  'auth/invalid-email': 'Format d\'adresse email invalide. Veuillez entrer une adresse email valide.',
  'auth/too-many-requests': 'Trop de tentatives de connexion infructueuses. Veuillez réessayer plus tard ou réinitialiser votre mot de passe.',
  'auth/network-request-failed': 'Erreur réseau. Veuillez vérifier votre connexion internet et réessayer.',
  'auth/popup-closed-by-user': 'La fenêtre d\'authentification a été fermée avant la fin du processus de connexion.',
  'auth/requires-recent-login': 'Cette opération nécessite une connexion récente. Veuillez vous reconnecter avant de réessayer.',
  'auth/user-disabled': 'Ce compte a été désactivé. Veuillez contacter le support pour obtenir de l\'aide.',
  'auth/user-token-expired': 'Votre session a expiré. Veuillez vous reconnecter.',
  'auth/web-storage-unsupported': 'Ce navigateur ne prend pas en charge le stockage web. Veuillez essayer un autre navigateur.',
  'auth/operation-not-allowed': 'Cette opération n\'est pas autorisée. Veuillez contacter le support.',
  'auth/account-exists-with-different-credential': 'Un compte existe déjà avec la même adresse email mais des identifiants de connexion différents.',
  'auth/invalid-verification-code': 'Code de vérification invalide. Veuillez réessayer.',
  'auth/invalid-verification-id': 'ID de vérification invalide. Veuillez réessayer.',
  'auth/missing-verification-code': 'Code de vérification manquant. Veuillez réessayer.',
  'auth/missing-verification-id': 'ID de vérification manquant. Veuillez réessayer.',
  'auth/captcha-check-failed': 'La vérification CAPTCHA a échoué. Veuillez réessayer.',
  'auth/missing-recaptcha-token': 'Jeton reCAPTCHA manquant. Veuillez réessayer.',
  'auth/invalid-recaptcha-token': 'Jeton reCAPTCHA invalide. Veuillez réessayer.',
  'auth/invalid-phone-number': 'Numéro de téléphone invalide. Veuillez entrer un numéro de téléphone valide.',
  'auth/missing-phone-number': 'Numéro de téléphone manquant. Veuillez entrer un numéro de téléphone.',
  'auth/quota-exceeded': 'Quota dépassé. Veuillez réessayer plus tard.',
  'auth/cancelled-popup-request': 'Une seule demande de popup est autorisée à la fois.',
  'auth/popup-blocked': 'La popup a été bloquée par le navigateur. Veuillez autoriser les popups pour ce site.',
  'auth/unauthorized-domain': 'Ce domaine n\'est pas autorisé pour les opérations OAuth.',
  'auth/invalid-action-code': 'Code d\'action invalide. Le code a peut-être expiré ou a déjà été utilisé.',
  'auth/expired-action-code': 'Le code d\'action a expiré. Veuillez en demander un nouveau.',
  'auth/invalid-continue-uri': 'URL de continuation invalide. Veuillez réessayer.',
  'auth/missing-continue-uri': 'URL de continuation manquante. Veuillez réessayer.',
  'auth/missing-iframe-start': 'Une erreur interne s\'est produite. Veuillez réessayer plus tard.',
  'auth/missing-android-pkg-name': 'Nom du package Android manquant.',
  'auth/missing-app-credential': 'Identifiant d\'application manquant.',
  'auth/invalid-app-credential': 'Identifiant d\'application invalide.',
  'auth/session-expired': 'Le code SMS a expiré. Veuillez renvoyer le code de vérification pour réessayer.',
};

/**
 * Handles Firebase authentication errors and returns user-friendly error messages
 * @param error The Firebase error object or error message
 * @param language The language code ('en' or 'fr')
 * @returns A user-friendly error message
 */
export const handleFirebaseAuthError = (error: unknown, language: string = 'en'): string => {
  // Default error message
  const defaultErrorEN = 'An error occurred during authentication. Please try again.';
  const defaultErrorFR = 'Une erreur s\'est produite lors de l\'authentification. Veuillez réessayer.';
  
  // If error is a FirebaseError, extract the code and return the corresponding message
  if (error instanceof Error) {
    if ('code' in error) {
      const firebaseError = error as FirebaseError;
      const errorCode = firebaseError.code as FirebaseAuthErrorCode;
      
      // Return the error message in the specified language
      if (language === 'fr') {
        return errorMessagesFR[errorCode] || defaultErrorFR;
      } else {
        return errorMessagesEN[errorCode] || defaultErrorEN;
      }
    }
    
    // If it's a regular Error but not a FirebaseError, return the error message
    return error.message;
  }
  
  // If error is a string, return it
  if (typeof error === 'string') {
    return error;
  }
  
  // For any other type of error, return the default message
  return language === 'fr' ? defaultErrorFR : defaultErrorEN;
};
